import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';
import AttributeValueBadge from './AttributeValueBadge';
import {
  applySelectionRange,
  buildAttributeWeekSeries,
  getDiscreteBuckets,
  getNumericRangeForAttr,
  isDiscreteAttributeType,
  normalizeAttrValue,
  persistAttributeWeekValues,
} from '../../utils/blockCalendarAttributeUtils';
import api from '../../utils/axiosConfig';
import { resolveSchemaForType } from '../../utils/periodizationValueSchema';

const CHART_H = 220;
const PAD = { top: 16, right: 24, bottom: 36, left: 44 };

function valueKey(v) {
  if (v === null || v === undefined) return '__null__';
  return String(v);
}

function NumericSeriesChart({
  series,
  attr,
  draft,
  selected,
  onSelectWeek,
  onValueChange,
  range,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [plotW, setPlotW] = useState(480);
  const n = series.length;
  const w = plotW + PAD.left + PAD.right;
  const h = CHART_H + PAD.top + PAD.bottom;
  const plotH = CHART_H;
  const { min, max } = range;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      setPlotW(Math.max(120, width - PAD.left - PAD.right));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const xAt = (i) => PAD.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (val) => {
    if (val === null || val === undefined || Number.isNaN(Number(val))) {
      return PAD.top + plotH / 2;
    }
    const t = max === min ? 0.5 : (Number(val) - min) / (max - min);
    return PAD.top + plotH * (1 - t);
  };

  const points = series.map((row, i) => ({
    ...row,
    i,
    x: xAt(i),
    y: yAt(draft.get(row.weekStart) ?? row.value),
    val: draft.get(row.weekStart) ?? row.value,
  }));

  const pathD =
    points.length > 1
      ? points
          .filter((p) => p.val !== null && p.val !== undefined)
          .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
          .join(' ')
      : '';

  const yFromClient = (clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const scaleY = h / rect.height;
    const relY = (clientY - rect.top) * scaleY - PAD.top;
    const t = 1 - Math.max(0, Math.min(1, relY / plotH));
    const raw = min + t * (max - min);
    const step = resolveSchemaForType(attr).config?.numeric?.step ?? 1;
    return Math.round(raw / step) * step;
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const v = yFromClient(e.clientY);
      if (v == null) return;
      onValueChange(dragRef.current.weekStart, v, true);
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [min, max, attr, onValueChange]);

  return (
    <div
      ref={containerRef}
      className='w-full rounded-lg border border-border bg-muted/20 p-3'
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        className='block w-full select-none'
        style={{ height: h }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = PAD.top + plotH * (1 - t);
          const label = Math.round(min + t * (max - min));
          return (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y} y2={y} stroke='currentColor' strokeOpacity={0.12} />
              <text x={PAD.left - 8} y={y + 4} textAnchor='end' className='fill-muted-foreground text-[10px]'>
                {label}
              </text>
            </g>
          );
        })}
        {pathD ? (
          <path d={pathD} fill='none' stroke='hsl(var(--primary))' strokeWidth={2} strokeLinejoin='round' />
        ) : null}
        {points.map((p) => {
          const sel = selected.has(p.weekStart);
          const disabled = !p.editable;
          return (
            <g key={p.weekStart}>
              <circle
                cx={p.x}
                cy={p.y}
                r={sel ? 9 : 7}
                className={cn(
                  disabled ? 'fill-muted cursor-not-allowed' : 'fill-primary cursor-grab active:cursor-grabbing',
                  sel && 'stroke-2 stroke-amber-400'
                )}
                onMouseDown={(e) => {
                  if (disabled) return;
                  e.preventDefault();
                  dragRef.current = p;
                  onSelectWeek(p.weekStart, e);
                }}
              />
              <text
                x={p.x}
                y={h - 10}
                textAnchor='middle'
                className={cn(
                  'cursor-pointer fill-muted-foreground text-[10px]',
                  sel && 'fill-foreground font-semibold'
                )}
                onClick={(e) => onSelectWeek(p.weekStart, e)}
              >
                {p.weekLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function DiscreteBucketEditor({ series, attr, draft, selected, onSelectWeek, applyValueToWeeks }) {
  const buckets = getDiscreteBuckets(attr);
  const weekCols = series;

  const assignBucket = (weekStart, bucketKey) => {
    const targets =
      selected.size > 0 && selected.has(weekStart) ? selected : new Set([weekStart]);
    applyValueToWeeks(targets, bucketKey);
  };

  return (
    <div className='w-full rounded-lg border border-border'>
      <table className='w-full table-fixed border-collapse text-sm'>
        <thead>
          <tr className='border-b border-border bg-muted/40'>
            <th className='sticky left-0 z-[1] min-w-[140px] bg-muted/40 px-3 py-2 text-left text-xs font-medium text-muted-foreground'>
              Value
            </th>
            {weekCols.map((row) => (
              <th
                key={row.weekStart}
                className={cn(
                  'cursor-pointer px-1 py-2 text-center text-[10px] font-medium',
                  selected.has(row.weekStart) ? 'bg-primary/15 text-foreground' : 'text-muted-foreground'
                )}
                onClick={(e) => onSelectWeek(row.weekStart, e)}
              >
                {row.weekLabel}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr
              key={valueKey(bucket.key)}
              className='border-b border-border/60 hover:bg-muted/20'
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const week = e.dataTransfer.getData('text/week');
                if (week) assignBucket(week, bucket.key);
              }}
            >
              <td className='sticky left-0 z-[1] bg-card px-3 py-2'>
                <AttributeValueBadge type={attr} value={bucket.key} />
              </td>
              {weekCols.map((row) => {
                const val = draft.get(row.weekStart) ?? row.value;
                const match = valueKey(val) === valueKey(bucket.key);
                const sel = selected.has(row.weekStart);
                return (
                  <td
                    key={row.weekStart}
                    className={cn(
                      'px-1 py-2 text-center',
                      sel && 'bg-primary/10',
                      match && 'bg-accent/30'
                    )}
                    onClick={(e) => {
                      if (e.shiftKey || e.metaKey || e.ctrlKey) {
                        onSelectWeek(row.weekStart, e);
                        return;
                      }
                      if (row.editable) assignBucket(row.weekStart, bucket.key);
                    }}
                  >
                    {match ? (
                      <span
                        draggable={row.editable}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/week', row.weekStart);
                        }}
                        className='inline-flex cursor-grab'
                      >
                        <AttributeValueBadge type={attr} value={val} />
                      </span>
                    ) : (
                      <span className='text-muted-foreground/40'>·</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BlockAttributeSeriesModal({
  open,
  onOpenChange,
  block,
  attr,
  weeks,
  onSaved,
}) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [lastAnchor, setLastAnchor] = useState(null);
  const [draft, setDraft] = useState(() => new Map());

  const series = useMemo(
    () => (block && attr ? buildAttributeWeekSeries(block, weeks, attr) : []),
    [block, weeks, attr]
  );

  const discrete = attr ? isDiscreteAttributeType(attr) : false;
  const range = attr && !discrete ? getNumericRangeForAttr(attr) : { min: 0, max: 100 };

  useEffect(() => {
    if (!open || !series.length) return;
    const m = new Map();
    series.forEach((r) => m.set(r.weekStart, r.value));
    setDraft(m);
    setSelected(new Set());
    setLastAnchor(null);
  }, [open, block?.id, attr?.type_key, series]);

  const weekKeys = useMemo(() => series.map((r) => r.weekStart), [series]);

  const handleSelectWeek = useCallback(
    (weekStart, e) => {
      if (e.shiftKey && lastAnchor) {
        setSelected((prev) => applySelectionRange(weekKeys, lastAnchor, weekStart, prev));
      } else if (e.metaKey || e.ctrlKey) {
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(weekStart)) next.delete(weekStart);
          else next.add(weekStart);
          return next;
        });
        setLastAnchor(weekStart);
      } else {
        setSelected(new Set([weekStart]));
        setLastAnchor(weekStart);
      }
    },
    [lastAnchor, weekKeys]
  );

  const applyValueToWeeks = useCallback(
    (targetWeeks, value) => {
      setDraft((prev) => {
        const next = new Map(prev);
        for (const w of targetWeeks) {
          const row = series.find((r) => r.weekStart === w);
          if (!row?.editable) continue;
          if (row.targetType === 'phase') {
            series
              .filter((r) => r.phaseId === row.phaseId && r.editable)
              .forEach((r) => next.set(r.weekStart, value));
          } else {
            next.set(w, value);
          }
        }
        return next;
      });
    },
    [series]
  );

  const applyToSelection = useCallback(
    (weekStart, value, extendSelection) => {
      const targets =
        extendSelection && selected.size > 0
          ? selected
          : selected.size > 0 && selected.has(weekStart)
            ? selected
            : new Set([weekStart]);
      applyValueToWeeks(targets, value);
    },
    [selected, applyValueToWeeks]
  );

  const selectAllEditable = () => {
    setSelected(new Set(series.filter((r) => r.editable).map((r) => r.weekStart)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistAttributeWeekValues(api, attr, series, draft);
      onOpenChange(false);
      await onSaved?.();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!block || !attr) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[88vw] gap-0 p-0' priority='high'>
        <DialogHeader className='border-b border-border px-6 py-4'>
          <DialogTitle className='text-lg'>{attr.name}</DialogTitle>
          <DialogDescription>
            {block.name} · {t('periodization.calendar.attrSeriesHint')}
          </DialogDescription>
          <div className='flex flex-wrap items-center gap-2 pt-2'>
            <Badge variant='secondary' className='text-xs'>
              {selected.size
                ? t('periodization.calendar.weeksSelected', { count: selected.size })
                : t('periodization.calendar.clickWeekToSelect')}
            </Badge>
            <Button type='button' size='sm' variant='outline' onClick={selectAllEditable}>
              {t('periodization.calendar.selectAllWeeks')}
            </Button>
            <Button type='button' size='sm' variant='ghost' onClick={clearSelection} disabled={!selected.size}>
              {t('periodization.calendar.clearSelection')}
            </Button>
            <span className='text-xs text-muted-foreground'>{t('periodization.calendar.multiSelectHint')}</span>
          </div>
        </DialogHeader>

        <div className='max-h-[min(70vh,560px)] overflow-auto px-6 py-4 w-full'>
          {discrete ? (
            <DiscreteBucketEditor
              series={series}
              attr={attr}
              draft={draft}
              selected={selected}
              onSelectWeek={handleSelectWeek}
              applyValueToWeeks={applyValueToWeeks}
            />
          ) : (
            <NumericSeriesChart
              series={series}
              attr={attr}
              draft={draft}
              selected={selected}
              range={range}
              onSelectWeek={handleSelectWeek}
              onValueChange={(weekStart, val) => applyToSelection(weekStart, normalizeAttrValue(val, attr), true)}
            />
          )}
        </div>

        <DialogFooter className='border-t border-border px-6 py-4'>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type='button' onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
